import axios from "axios";
import { API_BASE_URL } from "../../config";
import { ACCOUNT_BLOCKED_QUERY, isAccountBlockedError } from "../authentication/utils/accountBlocked";
import { PortalContextResponse } from "./contracts/PortalContextResponse";

export const PORTAL_TOKEN_KEY = "portal_access_token";
export const PORTAL_SESSION_KEY = "portal_session";

/**
 * Dedicated HTTP channel for the member portal. Deliberately separate from the
 * instructor axiosInstance: the portal token lives under its own storage key,
 * and a portal 401 must never trigger the instructor logout flow.
 */
const portalAxios = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

portalAxios.interceptors.request.use((requestConfig) => {
  const token = localStorage.getItem(PORTAL_TOKEN_KEY);
  if (token) {
    requestConfig.headers["Authorization"] = `Bearer ${token}`;
  }
  return requestConfig;
});

portalAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem(PORTAL_TOKEN_KEY)) {
      clearPortalSession();
      window.location.assign("/portal/login");
    } else if (isAccountBlockedError(error)) {
      // Blocked by the platform (US-111): at sign-in or on any later request, the same line.
      clearPortalSession();
      window.location.assign(`/portal/login?${ACCOUNT_BLOCKED_QUERY}`);
    }
    return Promise.reject(error);
  },
);

export const clearPortalSession = () => {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_SESSION_KEY);
};

/** What a portal sign-in keeps next to its token: who is signed in and the other rows of the email. */
export interface StoredPortalSession {
  memberId: string;
  firstName: string;
  expiresAtUtc: string;
  /** Every member row the email reaches (PD-0033); empty for one row or a session stored before US-69. */
  contexts: PortalContextResponse[];
}

/** Stores a portal token and its session under the keys the interceptor and the slice rehydrate from. */
export const storePortalSession = (accessToken: string, session: StoredPortalSession): void => {
  localStorage.setItem(PORTAL_TOKEN_KEY, accessToken);
  localStorage.setItem(PORTAL_SESSION_KEY, JSON.stringify(session));
};

export default portalAxios;
